import { useState, useEffect } from "react";
import api, { PART_STATUS_META, PART_STATUSES, derivePartStatus } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  RadioGroup,
  RadioGroupItem,
} from "@/components/ui/radio-group";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Wrench, Package, User, Car, Store } from "lucide-react";

const STATUSES = [
  { value: "new", label: "New" },
  { value: "in_progress", label: "In Progress" },
  { value: "ready", label: "Ready" },
  { value: "completed", label: "Completed" },
];

const emptyForm = {
  customer_name: "",
  customer_phone: "",
  customer_type: "customer",
  vehicle_plate: "",
  vehicle_make: "",
  vehicle_model: "",
  vehicle_year: "",
  complaint: "",
  notes: "",
  status: "new",
  labor_items: [],
  parts_items: [],
};

export default function WorkOrderFormDialog({ open, onOpenChange, editing, onSaved }) {
  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [parts, setParts] = useState([]);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const [c, v, p] = await Promise.all([
        api.get("/customers"),
        api.get("/vehicles"),
        api.get("/parts"),
      ]);
      setCustomers(c.data);
      setVehicles(v.data);
      setParts(p.data);
      if (editing) {
        // Resolve current customer + vehicle for pre-fill
        const cust = c.data.find((x) => x.id === editing.customer_id);
        const veh = v.data.find((x) => x.id === editing.vehicle_id);
        setForm({
          customer_name: cust?.name || "",
          customer_phone: cust?.phone || "",
          customer_type: cust?.type || "customer",
          vehicle_plate: veh?.license_plate || "",
          vehicle_make: veh?.make || "",
          vehicle_model: veh?.model || "",
          vehicle_year: veh?.year || "",
          complaint: editing.complaint || "",
          notes: editing.notes || "",
          status: editing.status || "new",
          labor_items: editing.labor_items || [],
          parts_items: editing.parts_items || [],
        });
      } else {
        setForm(emptyForm);
      }
    })();
  }, [open, editing]);

  // Autofill vehicle details when plate matches an existing vehicle
  const onPlateChange = (val) => {
    const trimmed = val.trim();
    const match = vehicles.find(
      (v) => v.license_plate && v.license_plate.toLowerCase() === trimmed.toLowerCase()
    );
    if (match) {
      const owner = customers.find((c) => c.id === match.customer_id);
      setForm((f) => ({
        ...f,
        vehicle_plate: val,
        vehicle_make: match.make || f.vehicle_make,
        vehicle_model: match.model || f.vehicle_model,
        vehicle_year: match.year || f.vehicle_year,
        customer_name: owner?.name || f.customer_name,
        customer_phone: owner?.phone || f.customer_phone,
        customer_type: owner?.type || f.customer_type,
      }));
    } else {
      setForm((f) => ({ ...f, vehicle_plate: val }));
    }
  };

  const onCustomerNameChange = (val) => {
    const match = customers.find(
      (c) => c.name && c.name.toLowerCase() === val.trim().toLowerCase()
    );
    setForm((f) => ({
      ...f,
      customer_name: val,
      customer_phone: match?.phone || f.customer_phone,
      customer_type: match?.type || f.customer_type,
    }));
  };

  const addLabor = () =>
    setForm({
      ...form,
      labor_items: [...form.labor_items, { description: "", hours: 0, done: false }],
    });
  const updateLabor = (idx, field, value) => {
    const arr = [...form.labor_items];
    arr[idx] = { ...arr[idx], [field]: value };
    setForm({ ...form, labor_items: arr });
  };
  const removeLabor = (idx) =>
    setForm({ ...form, labor_items: form.labor_items.filter((_, i) => i !== idx) });

  const addPartLine = () =>
    setForm({
      ...form,
      parts_items: [...form.parts_items, { part_id: null, name: "", quantity: 1, status: "pending", ordered: false }],
    });
  const updatePart = (idx, field, value) => {
    const arr = [...form.parts_items];
    arr[idx] = { ...arr[idx], [field]: value };
    setForm({ ...form, parts_items: arr });
  };
  const setPartFromInventory = (idx, partId) => {
    const p = parts.find((x) => x.id === partId);
    if (!p) return;
    updatePart(idx, "part_id", p.id);
    updatePart(idx, "name", p.name);
  };
  const removePartLine = (idx) =>
    setForm({ ...form, parts_items: form.parts_items.filter((_, i) => i !== idx) });

  const save = async () => {
    if (!form.customer_name.trim()) {
      toast.error("Customer name is required");
      return;
    }
    if (!form.vehicle_plate.trim()) {
      toast.error("Vehicle registration number is required");
      return;
    }
    const payload = {
      customer_name: form.customer_name.trim(),
      customer_phone: form.customer_phone.trim(),
      customer_type: form.customer_type || "customer",
      vehicle_plate: form.vehicle_plate.trim(),
      vehicle_make: form.vehicle_make.trim(),
      vehicle_model: form.vehicle_model.trim(),
      vehicle_year: parseInt(form.vehicle_year) || 0,
      complaint: form.complaint,
      notes: form.notes,
      status: form.status,
      labor_items: form.labor_items.map((i) => ({
        description: i.description || "",
        hours: parseFloat(i.hours) || 0,
        done: !!i.done,
      })),
      parts_items: form.parts_items.map((p) => {
        const status = ((p.status || "").toLowerCase()) ||
          (p.ordered ? "received" : "pending");
        return {
          part_id: p.part_id || null,
          name: p.name || "",
          quantity: parseInt(p.quantity) || 0,
          status,
          ordered: status !== "pending",
        };
      }),
    };
    try {
      if (editing) {
        await api.put(`/work-orders/${editing.id}`, payload);
        toast.success("Repair order updated");
      } else {
        await api.post("/work-orders", payload);
        toast.success("Repair order created");
      }
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save repair order");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="rounded-2xl max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="work-order-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">
            {editing ? `Edit ${editing.order_number}` : "New Repair Order"}
          </DialogTitle>
        </DialogHeader>

        {/* Autocomplete data lists */}
        <datalist id="customer-names-list">
          {customers.map((c) => (
            <option key={c.id} value={c.name} />
          ))}
        </datalist>
        <datalist id="vehicle-plates-list">
          {vehicles.map((v) => (
            <option
              key={v.id}
              value={v.license_plate}
              label={`${v.year} ${v.make} ${v.model}`}
            />
          ))}
        </datalist>

        <div className="space-y-5 py-2">
          {/* Vehicle Registration Section */}
          <div className="p-4 rounded-xl bg-[#FAF9F6] border border-warm">
            <div className="text-xs uppercase tracking-widest text-[#3D405B] font-semibold mb-3 flex items-center gap-2">
              <Car className="w-3.5 h-3.5" /> Vehicle Registration
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
              <div>
                <Label className="text-sm font-semibold text-[#3D405B]">
                  Registration Number *
                </Label>
                <Input
                  data-testid="wo-vehicle-plate-input"
                  value={form.vehicle_plate}
                  onChange={(e) => onPlateChange(e.target.value)}
                  placeholder="e.g. ABC-1234"
                  list="vehicle-plates-list"
                  className="mt-1.5 rounded-lg border-2 border-warm bg-white font-mono uppercase"
                  autoComplete="off"
                />
                <div className="text-xs text-slate-muted mt-1">
                  Type a plate — existing vehicles auto-fill.
                </div>
              </div>
              <div className="grid grid-cols-[80px_1fr] gap-2">
                <div>
                  <Label className="text-sm font-semibold text-[#3D405B]">Year</Label>
                  <Input
                    data-testid="wo-vehicle-year-input"
                    type="number"
                    value={form.vehicle_year}
                    onChange={(e) =>
                      setForm({ ...form, vehicle_year: e.target.value })
                    }
                    placeholder="2020"
                    className="mt-1.5 rounded-lg border-2 border-warm bg-white"
                  />
                </div>
                <div>
                  <Label className="text-sm font-semibold text-[#3D405B]">Make</Label>
                  <Input
                    data-testid="wo-vehicle-make-input"
                    value={form.vehicle_make}
                    onChange={(e) =>
                      setForm({ ...form, vehicle_make: e.target.value })
                    }
                    placeholder="Toyota"
                    className="mt-1.5 rounded-lg border-2 border-warm bg-white"
                  />
                </div>
              </div>
            </div>
            <div>
              <Label className="text-sm font-semibold text-[#3D405B]">Model</Label>
              <Input
                data-testid="wo-vehicle-model-input"
                value={form.vehicle_model}
                onChange={(e) =>
                  setForm({ ...form, vehicle_model: e.target.value })
                }
                placeholder="Camry"
                className="mt-1.5 rounded-lg border-2 border-warm bg-white"
              />
            </div>
          </div>

          {/* Customer Section */}
          <div className="p-4 rounded-xl bg-[#FAF9F6] border border-warm">
            <div className="text-xs uppercase tracking-widest text-[#3D405B] font-semibold mb-3 flex items-center gap-2">
              <User className="w-3.5 h-3.5" /> Customer
            </div>
            <RadioGroup
              value={form.customer_type || "customer"}
              onValueChange={(val) => setForm({ ...form, customer_type: val })}
              className="grid grid-cols-2 gap-3 mb-4"
              data-testid="wo-customer-type-radio"
            >
              <label
                className={`flex items-center gap-2 p-2.5 rounded-lg border-2 cursor-pointer transition-colors ${
                  (form.customer_type || "customer") === "customer"
                    ? "border-[#1A1A1A] bg-white"
                    : "border-warm bg-white/60"
                }`}
              >
                <RadioGroupItem value="customer" data-testid="wo-type-radio-customer" />
                <User className="w-4 h-4 text-slate-muted" />
                <span className="text-sm font-medium text-[#2B2D42]">Customer</span>
              </label>
              <label
                className={`flex items-center gap-2 p-2.5 rounded-lg border-2 cursor-pointer transition-colors ${
                  form.customer_type === "dealer"
                    ? "border-[#E5A82E] bg-[#F4D35E]/10"
                    : "border-warm bg-white/60"
                }`}
              >
                <RadioGroupItem value="dealer" data-testid="wo-type-radio-dealer" />
                <Store className="w-4 h-4 text-[#8B6914]" />
                <span className="text-sm font-medium text-[#2B2D42]">Dealer</span>
              </label>
            </RadioGroup>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-semibold text-[#3D405B]">Name *</Label>
                <Input
                  data-testid="wo-customer-name-input"
                  value={form.customer_name}
                  onChange={(e) => onCustomerNameChange(e.target.value)}
                  placeholder="Jane Doe"
                  list="customer-names-list"
                  className="mt-1.5 rounded-lg border-2 border-warm bg-white"
                  autoComplete="off"
                />
              </div>
              <div>
                <Label className="text-sm font-semibold text-[#3D405B]">Phone</Label>
                <Input
                  data-testid="wo-customer-phone-input"
                  value={form.customer_phone}
                  onChange={(e) =>
                    setForm({ ...form, customer_phone: e.target.value })
                  }
                  placeholder="(555) 555-1234"
                  className="mt-1.5 rounded-lg border-2 border-warm bg-white"
                />
              </div>
            </div>
          </div>

          <div>
            <Label className="text-sm font-semibold text-[#3D405B]">
              Customer Complaint
            </Label>
            <Textarea
              data-testid="wo-complaint-input"
              value={form.complaint}
              onChange={(e) => setForm({ ...form, complaint: e.target.value })}
              rows={2}
              placeholder="Grinding noise when braking..."
              className="mt-1.5 rounded-lg border-2 border-warm"
            />
          </div>

          {/* Repair tasks */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-semibold text-[#3D405B] uppercase tracking-wider flex items-center gap-2">
                <Wrench className="w-4 h-4" /> Repair Tasks
              </Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addLabor}
                data-testid="add-labor-btn"
                className="rounded-lg border-2 border-warm"
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Add task
              </Button>
            </div>
            <div className="space-y-2">
              {form.labor_items.map((l, idx) => (
                <div
                  key={idx}
                  className="grid grid-cols-[auto_1fr_90px_40px] gap-2 items-center bg-[#FAF9F6] rounded-lg p-2 border border-warm"
                  data-testid={`labor-row-${idx}`}
                >
                  <Checkbox
                    checked={l.done}
                    onCheckedChange={(v) => updateLabor(idx, "done", !!v)}
                    data-testid={`labor-done-${idx}`}
                    className="ml-1"
                  />
                  <Input
                    value={l.description}
                    onChange={(e) => updateLabor(idx, "description", e.target.value)}
                    placeholder="Brake pad replacement"
                    className="rounded-md border-warm bg-white"
                  />
                  <Input
                    type="number"
                    step="0.25"
                    value={l.hours}
                    onChange={(e) => updateLabor(idx, "hours", e.target.value)}
                    placeholder="Est. hrs"
                    className="rounded-md border-warm bg-white"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeLabor(idx)}
                    className="rounded-md text-[#E07A5F]"
                    data-testid={`remove-labor-${idx}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              {form.labor_items.length === 0 && (
                <div className="text-sm text-slate-muted italic px-2">
                  No tasks added
                </div>
              )}
            </div>
          </div>

          {/* Parts to order */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-semibold text-[#3D405B] uppercase tracking-wider flex items-center gap-2">
                <Package className="w-4 h-4" /> Parts to Order
              </Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addPartLine}
                data-testid="add-part-line-btn"
                className="rounded-lg border-2 border-warm"
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Add part
              </Button>
            </div>
            <div className="space-y-2">
              {form.parts_items.map((p, idx) => {
                const currentStatus = derivePartStatus(p);
                const meta = PART_STATUS_META[currentStatus] || PART_STATUS_META.pending;
                return (
                <div
                  key={idx}
                  className="grid grid-cols-[1fr_1fr_60px_auto_40px] gap-2 items-center bg-[#FAF9F6] rounded-lg p-2 border border-warm"
                  data-testid={`part-row-${idx}`}
                >
                  <Select
                    value={p.part_id || ""}
                    onValueChange={(val) => setPartFromInventory(idx, val)}
                  >
                    <SelectTrigger className="rounded-md bg-white border-warm">
                      <SelectValue placeholder="From inventory..." />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl">
                      {parts.map((x) => (
                        <SelectItem key={x.id} value={x.id}>
                          {x.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    value={p.name}
                    onChange={(e) => updatePart(idx, "name", e.target.value)}
                    placeholder="Or custom part name"
                    className="rounded-md border-warm bg-white"
                  />
                  <Input
                    type="number"
                    value={p.quantity}
                    onChange={(e) => updatePart(idx, "quantity", e.target.value)}
                    placeholder="Qty"
                    className="rounded-md border-warm bg-white"
                  />
                  <Select
                    value={currentStatus}
                    onValueChange={(val) => {
                      updatePart(idx, "status", val);
                      updatePart(idx, "ordered", val !== "pending");
                    }}
                  >
                    <SelectTrigger
                      data-testid={`part-status-select-${idx}`}
                      className={`h-auto py-1 px-2.5 rounded-full text-xs font-semibold uppercase tracking-wider border w-auto gap-1.5 focus:ring-2 focus:ring-[#E5A82E] [&>svg]:h-3 [&>svg]:w-3 [&>svg]:opacity-70 ${meta.cls}`}
                    >
                      <span>{meta.label}</span>
                    </SelectTrigger>
                    <SelectContent className="rounded-xl">
                      {PART_STATUSES.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removePartLine(idx)}
                    className="rounded-md text-[#E07A5F]"
                    data-testid={`remove-part-line-${idx}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                );
              })}
              {form.parts_items.length === 0 && (
                <div className="text-sm text-slate-muted italic px-2">
                  No parts needed
                </div>
              )}
              <div className="text-xs text-slate-muted px-2">
                Pending · Order Placed · Received
              </div>
            </div>
          </div>

          <div>
            <Label className="text-sm font-semibold text-[#3D405B]">Status</Label>
            <Select
              value={form.status}
              onValueChange={(val) => setForm({ ...form, status: val })}
            >
              <SelectTrigger
                data-testid="wo-status-select"
                className="mt-1.5 rounded-lg border-2 border-warm"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                {STATUSES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-sm font-semibold text-[#3D405B]">Internal Notes</Label>
            <Textarea
              data-testid="wo-notes-input"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
              className="mt-1.5 rounded-lg border-2 border-warm"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-xl border-2 border-warm"
            data-testid="cancel-wo-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={save}
            className="bg-[#1A1A1A] hover:bg-[#2D2D2D] text-white rounded-xl"
            data-testid="save-wo-btn"
          >
            {editing ? "Save Changes" : "Create Repair Order"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
